import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Alert } from 'antd';
import { Button, FormField, Input } from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { authApi, type OtpChannel } from '../api/auth-api';
import { verifySchema, type VerifyFormValues } from '../model/schemas';
import { applyTokens } from '../model/auth-actions';
import { mapAuthError } from '../lib/map-auth-error';

const RESEND_COOLDOWN_SECONDS = 60;

interface VerifyFormProps {
  identity: string;
  channel: OtpChannel;
}

export function VerifyForm({ identity, channel }: VerifyFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const form = useForm<VerifyFormValues>({
    resolver: zodResolver(verifySchema),
    defaultValues: { code: '' },
  });

  const verifyMutation = useMutation({
    mutationFn: (values: VerifyFormValues) =>
      authApi.verify({ identity, channel, code: values.code }),
    onSuccess: (tokens) => {
      applyTokens(tokens);
      navigate(ROUTES.root, { replace: true });
    },
    onError: (error) => setSubmitError(mapAuthError(error)),
  });

  const resendMutation = useMutation({
    mutationFn: () => authApi.requestOtp({ identity, channel }),
    onSuccess: () => setCooldown(RESEND_COOLDOWN_SECONDS),
    onError: (error) => setSubmitError(mapAuthError(error)),
  });

  return (
    <FormProvider {...form}>
    <form
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((v) => {
        setSubmitError(null);
        verifyMutation.mutate(v);
      })}
      noValidate
    >
      <FormField label={t('common.code')} error={form.formState.errors.code?.message}>
        {(p) => (
          <Input
            {...p}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            className="text-center text-lg tracking-[0.4em]"
            {...form.register('code')}
          />
        )}
      </FormField>

      {submitError ? <Alert type="error" showIcon message={submitError} /> : null}

      <Button type="submit" loading={verifyMutation.isPending}>
        {t('auth.verify.submit')}
      </Button>

      <Button
        type="button"
        variant="ghost"
        disabled={cooldown > 0 || resendMutation.isPending}
        loading={resendMutation.isPending}
        onClick={() => resendMutation.mutate()}
      >
        {cooldown > 0
          ? t('auth.verify.resendIn', { seconds: cooldown })
          : t('auth.verify.resend')}
      </Button>
    </form>
    </FormProvider>
  );
}
